import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from '@tanstack/react-router';
import { Logo } from './Logo.jsx';
import { firebaseService } from '../services/firebaseService';
import { posthogService } from '../services/posthogService';
import { storage } from '../utils/utils';

/**
 * Application header component - Nordic minimal design with auth-aware navigation
 */
function Header() {
  const location = useLocation();
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [hasInProgressSession, setHasInProgressSession] = useState(false);

  useEffect(() => {
    checkAuthStatus();
  }, [location.pathname]);

  // Listen for auth changes
  useEffect(() => {
    const handleAuthChange = () => {
      console.log('[Header] Auth changed event received, re-checking...');
      checkAuthStatus();
    };
    
    window.addEventListener('auth-changed', handleAuthChange);
    return () => window.removeEventListener('auth-changed', handleAuthChange);
  }, []);

  const checkAuthStatus = async () => {
    let currentUser = null;
    try {
      setLoading(true);
      currentUser = await firebaseService.getCurrentUser();
      setIsAuthenticated(!!currentUser);
      
      // Check for in-progress sessions
      if (currentUser) {
        try {
          const { default: apiService } = await import('../services/apiService');
          const idToken = await firebaseService.getIdToken();
          const sessions = await apiService.getUserSessions(idToken);
          const inProgress = sessions.some(s => s.status === 'processing');
          setHasInProgressSession(inProgress);
        } catch (error) {
          console.error('[Header] Failed to check sessions:', error);
        }
      }
    } catch (error) {
      console.error('[Header] Auth check error:', error);
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  };

  const handleSignOut = async () => {
    try {
      // Track sign out event
      posthogService.signOut();
      
      // Sign out from Firebase
      await firebaseService.signOut();
      
      // Clean up all local storage
      storage.clear();
      
      // Reload the page to reset all state
      window.location.href = '/';
    } catch (error) {
      console.error('[Header] Sign out error:', error);
      // Even if sign out fails, clear local storage and reload
      storage.clear();
      window.location.href = '/';
    }
  };

  return (
    <header className='text-center mb-6 sm:mb-8 p-4 sm:p-6 md:p-8 bg-white rounded-xl sm:rounded-2xl shadow-xl'>
      <div className='mb-4 sm:mb-6'>
        <div className='flex justify-center items-center mb-2 sm:mb-3'>
          <Logo />
        </div>
        <p className='text-xs sm:text-sm text-gray-600 max-w-2xl mx-auto px-4'>
          AI-powered credit card recommendations for UAE
        </p>
      </div>

      {/* Navigation - Only show when not loading */}
      {!loading && (
        <nav className='flex justify-center items-center gap-2 flex-wrap px-2'>
          {hasInProgressSession ? (
            <div className='px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg text-xs sm:text-sm font-medium bg-gray-100 text-gray-400 cursor-not-allowed min-h-[44px] flex items-center justify-center'>
              New Analysis (Processing...)
            </div>
          ) : (
            <Link
              to='/'
              className={`px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg text-xs sm:text-sm font-medium transition-colors touch-manipulation min-h-[44px] flex items-center justify-center ${
                location.pathname === '/' ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-100 active:bg-gray-200'
              }`}
            >
              New Analysis
            </Link>
          )}
          
          {isAuthenticated && (
            <>
              <Link
                to='/recommendations'
                className={`px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg text-xs sm:text-sm font-medium transition-colors touch-manipulation min-h-[44px] flex items-center justify-center whitespace-nowrap ${
                  location.pathname === '/recommendations' ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-100 active:bg-gray-200'
                }`}
              >
                My Recommendations
              </Link>
              
              <Link
                to='/profile'
                className={`px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg text-xs sm:text-sm font-medium transition-colors touch-manipulation min-h-[44px] flex items-center justify-center ${
                  location.pathname === '/profile' ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-100 active:bg-gray-200'
                }`}
              >
                Profile
              </Link>
              
              <button
                onClick={handleSignOut}
                className='px-3 sm:px-4 cursor-pointer py-2 sm:py-2.5 rounded-lg text-xs sm:text-sm font-medium text-gray-600 hover:bg-gray-100 active:bg-gray-200 transition-colors touch-manipulation min-h-[44px] flex items-center justify-center'
              >
                Sign Out
              </button>
            </>
          )}
        </nav>
      )}
    </header>
  );
}

export default Header;
